<template>
  <div v-if="shouldShow" class="debug-logger">
    <div class="debug-header">
      <h4>🔍 Debug Logger ({{ logs.length }})</h4>
      <div class="debug-controls">
        <button class="clear-logs" @click="clearLogs">Clear</button>
        <button class="copy-logs" @click="copyLogs">Copy</button>
        <button class="minimize-btn" @click="toggleMinimized">
          {{ isMinimized ? '📖' : '📕' }}
        </button>
        <button class="close-btn" @click="close">✕</button>
      </div>
    </div>
    <div v-if="!isMinimized" class="logs-container">
      <div v-for="(log, index) in logs" :key="index" class="log-entry" :class="log.level">
        <div class="log-header">
          <span class="log-number">#{{ index + 1 }}</span>
          <span class="log-timestamp">{{ formatTimestamp(log.timestamp) }}</span>
        </div>
        <div class="log-message">{{ log.message }}</div>
        <div v-if="Object.keys(log.data).length > 0" class="log-data">
          <div class="data-header">📊 Data:</div>
          <pre class="data-content">{{ formatLogData(log.data) }}</pre>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  export default {
    name: 'DebugLogger',
    data() {
      return {
        isEnabled: false,
        isMinimized: false,
        logs: [],
      }
    },
    computed: {
      shouldShow() {
        return this.isEnabled && this.logs.length > 0
      },
    },
    mounted() {
      // Listen for global debug events
      window.addEventListener('debug-log', this.handleDebugLog)
      window.addEventListener('debug-clear', this.clearLogs)
      window.addEventListener('debug-toggle', this.toggleEnabled)

      // Sync with SDK state
      if (window.debugger) {
        this.isEnabled = window.debugger.isEnabled
        console.log('DebugLogger mounted - isEnabled:', this.isEnabled)
        // Clear logs if disabled
        if (!this.isEnabled) {
          this.logs = []
        }
      }
    },
    beforeDestroy() {
      window.removeEventListener('debug-log', this.handleDebugLog)
      window.removeEventListener('debug-clear', this.clearLogs)
      window.removeEventListener('debug-toggle', this.toggleEnabled)
    },
    methods: {
      handleDebugLog(event) {
        // Only add logs if we're enabled
        if (this.isEnabled) {
          this.addLog(event.detail.level, event.detail.message, event.detail.data)
        }
      },

      addLog(level, message, data = {}) {
        this.logs.push({
          level,
          message,
          data,
          timestamp: new Date(),
        })

        // Keep only last 100 logs to prevent memory issues
        if (this.logs.length > 100) {
          this.logs = this.logs.slice(-100)
        }
      },

      clearLogs() {
        this.logs = []
      },

      async copyLogs() {
        try {
          const logsJson = JSON.stringify(this.logs, null, 2)
          await navigator.clipboard.writeText(logsJson)
          console.log('📋 Debug logs copied to clipboard')

          // Show a brief visual feedback
          const copyBtn = document.querySelector('.copy-logs')
          if (copyBtn) {
            const originalText = copyBtn.textContent
            copyBtn.textContent = '✓ Copied'
            copyBtn.style.background = '#28a745'
            setTimeout(() => {
              copyBtn.textContent = originalText
              copyBtn.style.background = '#6c757d'
            }, 1500)
          }
        } catch (error) {
          console.error('Failed to copy logs to clipboard:', error)
          alert('Failed to copy to clipboard. Logs are available in the browser console.')
        }
      },

      toggleMinimized() {
        this.isMinimized = !this.isMinimized
      },

      close() {
        this.isEnabled = false
        // Also disable the SDK
        if (window.debugger) {
          window.debugger.off()
        }
      },

      toggleEnabled() {
        // Sync with SDK state instead of toggling locally
        if (window.debugger) {
          this.isEnabled = window.debugger.isEnabled
          // Clear logs when disabled
          if (!this.isEnabled) {
            this.logs = []
          }
        }
      },

      formatTimestamp(timestamp) {
        return timestamp.toLocaleTimeString()
      },

      formatLogData(data) {
        if (typeof data === 'object' && data !== null) {
          return JSON.stringify(data, null, 2)
        }
        return String(data)
      },
    },
  }
</script>

<style scoped>
  .debug-logger {
    position: fixed;
    top: 20px;
    right: 20px;
    width: 400px;
    max-width: 90vw;
    background: #f8f9fa;
    border: 1px solid #dee2e6;
    border-radius: 8px;
    padding: 15px;
    font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
    font-size: 13px;
    max-height: 500px;
    overflow-y: auto;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    z-index: 10000;
  }

  .debug-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 15px;
    padding-bottom: 10px;
    border-bottom: 2px solid #e9ecef;
  }

  .debug-header h4 {
    margin: 0;
    color: #495057;
    font-size: 16px;
  }

  .debug-controls {
    display: flex;
    gap: 8px;
  }

  .debug-controls button {
    background: #6c757d;
    color: white;
    border: none;
    padding: 4px 8px;
    border-radius: 4px;
    cursor: pointer;
    font-size: 12px;
    transition: background-color 0.2s;
  }

  .debug-controls button:hover {
    background: #5a6268;
  }

  .clear-logs {
    background: #dc3545 !important;
  }

  .clear-logs:hover {
    background: #c82333 !important;
  }

  .copy-logs {
    background: #17a2b8 !important;
  }

  .copy-logs:hover {
    background: #138496 !important;
  }

  .close-btn {
    background: #6c757d !important;
  }

  .close-btn:hover {
    background: #5a6268 !important;
  }

  .logs-container {
    max-height: 400px;
    overflow-y: auto;
  }

  .log-entry {
    margin: 8px 0;
    padding: 12px;
    border-radius: 6px;
    border-left: 4px solid #ccc;
    background: white;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  }

  .log-entry.info {
    background: #e3f2fd;
    border-left-color: #2196f3;
  }

  .log-entry.success {
    background: #e8f5e8;
    border-left-color: #4caf50;
  }

  .log-entry.warn {
    background: #fff3e0;
    border-left-color: #ff9800;
  }

  .log-entry.error {
    background: #ffebee;
    border-left-color: #f44336;
  }

  .log-header {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 8px;
    font-weight: 500;
  }

  .log-number {
    background: #6c757d;
    color: white;
    padding: 2px 6px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
  }

  .log-timestamp {
    color: #6c757d;
    font-size: 11px;
    font-family: monospace;
  }

  .log-message {
    color: #212529;
    margin-bottom: 8px;
    line-height: 1.4;
  }

  .log-data {
    margin-top: 8px;
    border-top: 1px solid rgba(0, 0, 0, 0.1);
    padding-top: 8px;
  }

  .data-header {
    font-weight: bold;
    color: #495057;
    margin-bottom: 5px;
    font-size: 11px;
  }

  .data-content {
    background: #f8f9fa;
    border: 1px solid #e9ecef;
    border-radius: 4px;
    padding: 8px;
    margin: 0;
    font-size: 11px;
    font-family: 'Courier New', monospace;
    max-height: 150px;
    overflow-y: auto;
    white-space: pre-wrap;
    word-break: break-word;
  }
</style>
